import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getTrainingMetrics, getTrainingPlans } from '../api/athlete';
import { TrainingMetrics, TrainingPlan } from '../types/athlete';
import DashboardStats from './dashboard/DashboardStats';
import RaceCountdownWidget from './dashboard/RaceCountdownWidget';
import { Trophy, Plus } from 'lucide-react';
import { calculateDaysUntilRace } from '../lib/utils/dateUtils';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState<TrainingMetrics | null>(null);
  const [upcomingRaces, setUpcomingRaces] = useState<TrainingPlan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    let isMounted = true;
    const loadData = async () => {
      try {
        const [metricsData, plansData] = await Promise.all([
          getTrainingMetrics(user.id),
          getTrainingPlans(user.id),
        ]);

        if (isMounted) {
          setMetrics(metricsData);

          // Filter to upcoming races (future end_date) and sort by date
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          const upcoming = plansData
            .filter(plan => {
              const raceDate = new Date(plan.end_date);
              raceDate.setHours(0, 0, 0, 0);
              return raceDate >= today && (plan.status === 'active' || plan.status === 'draft');
            })
            .sort((a, b) => {
              const daysA = calculateDaysUntilRace(a.end_date);
              const daysB = calculateDaysUntilRace(b.end_date);
              return daysA - daysB;
            })
            .slice(0, 3); // Show top 3 upcoming races

          setUpcomingRaces(upcoming);
        }
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadData();
    return () => { isMounted = false; };
  }, [user]);

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Welcome{user ? `, ${user.email?.split('@')[0] || ''}` : ''}</h1>
        <p className="text-muted">Track your training, plan your races, and achieve your goals.</p>
      </div>

      {/* Race Countdown Widget */}
      {!loading && <div className="mb-8"><RaceCountdownWidget upcomingRaces={upcomingRaces} /></div>}

      {metrics ? (
        <DashboardStats metrics={metrics} loading={loading} />
      ) : loading ? (
        <div className="card-base p-8 text-center">
          <p className="text-muted">Loading your training data...</p>
        </div>
      ) : (
        <div className="card-base p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Trophy className="text-[#3b82f6]" size={24} />
            <h2 className="text-xl font-semibold">Getting Started</h2>
          </div>
          <p className="text-muted mb-4">
            Start by creating a training plan tailored to your race goals. You can track swims, bikes, runs, and strength sessions.
          </p>
          <Link
            to="/plans/new"
            className="inline-flex items-center px-4 py-2 bg-[#3b82f6] text-white rounded-lg hover:bg-[#60a5fa] transition-colors no-underline"
          >
            <Plus size={18} className="mr-2" />
            Create Your First Plan
          </Link>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
